import { useEffect, useState } from "react";

// Header Cell Component
const HeaderCell: React.FC<{ title: React.ReactNode }> = ({ title }) => (
  <th className="py-3 text-[14px] px-4 text-center font-medium text-[#F1935C] whitespace-nowrap">
    {title}
  </th>
);

export default function LoadPriceTable({
  selectedBlock,
  dataDam,
  dataGDam,
  dataDemand,
  dataRtm,
  loading,
  setData,
}: {
  selectedBlock: any[];
  dataDam: any[];
  dataGDam: any[];
  dataDemand: any[];
  dataRtm: any[];
  loading: boolean;
  setData: React.Dispatch<React.SetStateAction<any[]>>;
}) {
  const [damData, setDamData] = useState<any[]>([]);
  const [gdamData, setGdamData] = useState<any[]>([]);
  const [rtmData, setRtmData] = useState<any[]>([]);
  const [demandData, setDemandData] = useState<any[]>([]);
  const [filterData, setFilterData] = useState<any[]>([]);
  const [sortBy, setSortBy] = useState<string>("generator_name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  useEffect(() => {
    let filteredDamData = dataDam;
    if (selectedBlock.length > 0) {
      filteredDamData = filteredDamData.filter((item) =>
        selectedBlock
          .map((item) => Number(item.value))
          .includes(Number(item.time_slot))
      );
    }
    setDamData(filteredDamData);

    let filteredGDamData = dataGDam;
    if (selectedBlock.length > 0) {
      filteredGDamData = filteredGDamData.filter((item) =>
        selectedBlock
          .map((item) => Number(item.value))
          .includes(Number(item.time_slot))
      );
    }
    setGdamData(filteredGDamData);

    let filteredRtmData = dataRtm;
    if (selectedBlock.length > 0) {
      filteredRtmData = filteredRtmData.filter((item) =>
        selectedBlock
          .map((item) => Number(item.value))
          .includes(Number(item.time_slot))
      );
    }
    setRtmData(filteredRtmData);

    let filteredDemandData = dataDemand;
    if (selectedBlock.length > 0) {
      filteredDemandData = filteredDemandData.filter((item) =>
        selectedBlock
          .map((item) => Number(item.value))
          .includes(Number(item.time_slot))
      );
    }
    setDemandData(filteredDemandData);
  }, [dataDam, dataDemand, dataGDam, dataRtm, selectedBlock]);

  useEffect(() => {
    // Merge all datasets into one unified table
    const reference = damData.length
      ? damData
      : rtmData.length
      ? rtmData
      : gdamData.length
      ? gdamData
      : demandData;

    const merged = reference.map((item, idx) => ({
      time_slot: item.time_slot, // always comes from reference
      damPrice: damData[idx]?.wt_mcp_rs_mwh ?? null,
      gdamPrice: gdamData[idx]?.wt_mcp_rs_mwh ?? null,
      rtmPrice: rtmData[idx]?.wt_mcp_rs_mwh ?? null,
      demandPrice: demandData[idx]?.target_day ?? null,
    }));

    setFilterData(merged);
  }, [damData, rtmData, demandData, gdamData]);

  function handleSort(field: string): void {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setFilterData((prevData) => {
      const sorted = [...prevData].sort((a, b) => {
        let aValue = (a as any)[field];
        let bValue = (b as any)[field];
        // Try to compare as numbers if possible
        if (!isNaN(Number(aValue)) && !isNaN(Number(bValue))) {
          aValue = Number(aValue);
          bValue = Number(bValue);
        }
        if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
        if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
      return sorted;
    });
  }
  return (
    <>
      <div
        className="overflow-auto capitalize text-nowrap"
        style={{ maxHeight: "calc(100vh - 300px)" }}
      >
        <table id="my-table_load">
          <thead className="sticky top-0 z-10">
            <tr className="text-sm">
              {/* Basic Outage Details */}
              <HeaderCell
                title={
                  <span
                    className="cursor-pointer select-none flex items-center justify-center gap-1"
                    onClick={() => handleSort("time_slot")}
                  >
                    Block
                    {sortBy === "time_slot" &&
                      (sortOrder === "asc" ? "▲" : "▼")}
                  </span>
                }
              />
              <HeaderCell
                title={
                  <span
                    className="cursor-pointer select-none flex items-center justify-center gap-1"
                    onClick={() => handleSort("demandPrice")}
                  >
                    Anticipated Load (MW)
                    {sortBy === "demandPrice" &&
                      (sortOrder === "asc" ? "▲" : "▼")}
                  </span>
                }
              />
              <HeaderCell
                title={
                  <span
                    className="cursor-pointer select-none flex items-center justify-center gap-1"
                    onClick={() => handleSort("gdamPrice")}
                  >
                    Anticipated GDAM Rate (Rs/MWh)
                    {sortBy === "gdamPrice" &&
                      (sortOrder === "asc" ? "▲" : "▼")}
                  </span>
                }
              />
              <HeaderCell
                title={
                  <span
                    className="cursor-pointer select-none flex items-center justify-center gap-1"
                    onClick={() => handleSort("damPrice")}
                  >
                    Anticipated DAM Rate (Rs/MWh)
                    {sortBy === "damPrice" && (sortOrder === "asc" ? "▲" : "▼")}
                  </span>
                }
              />
              <HeaderCell
                title={
                  <span
                    className="cursor-pointer select-none flex items-center justify-center gap-1"
                    onClick={() => handleSort("rtmPrice")}
                  >
                    Anticipated RTM Rate (Rs/MWh)
                    {sortBy === "rtmPrice" && (sortOrder === "asc" ? "▲" : "▼")}
                  </span>
                }
              />
            </tr>
          </thead>
          <tbody className="text-[#8C8A96] font-normal text-[13px]">
            {filterData.map((item, idx) => (
              <tr
                key={idx}
                className="border-b border-gray-200 hover:bg-gray-100 text-center"
              >
                <td className="py-3 px-4 text-center whitespace-normal max-w-[200px]">
                  {item.time_slot ? item.time_slot : "-"}
                </td>
                <td className="py-3 px-4 text-center whitespace-normal max-w-[200px]">
                  {item.demandPrice ? item.demandPrice.toFixed(2) : "-"}
                </td>
                <td className="py-3 px-4 text-center whitespace-normal max-w-[200px]">
                  {item.gdamPrice ? item.gdamPrice.toFixed(2) : "-"}
                </td>
                <td className="py-3 px-4 text-center whitespace-normal max-w-[200px]">
                  {item.damPrice ? item.damPrice.toFixed(2) : "-"}
                </td>
                <td className="py-3 px-4 text-center whitespace-normal max-w-[200px]">
                  {item.rtmPrice ? item.rtmPrice.toFixed(2) : "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
